import React, { useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { useSettings } from '../../contexts/SettingsContext'

const Background = () => {
  const canvasRef = useRef(null)
  const { settings } = useSettings()

  useEffect(() => {
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    let animationFrameId
    
    const handleResize = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    
    window.addEventListener('resize', handleResize)
    handleResize()
    
    const starsArray = []
    const particlesArray = []
    const numberOfStars = 200
    const numberOfParticles = 80
    
    class Star {
      constructor() {
        this.x = Math.random() * canvas.width
        this.y = Math.random() * canvas.height
        this.z = Math.random() * 1000
        this.size = Math.random() * 2 + 0.5
        this.speed = Math.random() * 0.5 + 0.1
        this.color = this.getStarColor()
        this.opacity = Math.random() * 0.8 + 0.2
        this.twinkle = Math.random() * Math.PI * 2
      }
      
      getStarColor() {
        const colors = ['#ffffff', '#ffffcc', '#ffcc99', '#99ccff', '#cc99ff', '#00e5ff']
        return colors[Math.floor(Math.random() * colors.length)]
      }
      
      update() {
        this.z -= this.speed
        this.twinkle += 0.02
        
        if (this.z <= 0) {
          this.z = 1000
          this.x = Math.random() * canvas.width
          this.y = Math.random() * canvas.height
        }
      }
      
      draw() {
        const x = (this.x - canvas.width / 2) * (1000 / this.z) + canvas.width / 2
        const y = (this.y - canvas.height / 2) * (1000 / this.z) + canvas.height / 2
        const size = (1000 / this.z) * this.size
        const opacity = this.opacity * (1000 / this.z) * (0.5 + 0.5 * Math.sin(this.twinkle))
        
        if (x >= 0 && x <= canvas.width && y >= 0 && y <= canvas.height) {
          ctx.fillStyle = this.color
          ctx.globalAlpha = Math.min(opacity, 1)
          ctx.beginPath()
          ctx.arc(x, y, size, 0, Math.PI * 2)
          ctx.fill()
          
          // Add glow effect for brighter stars
          if (size > 1) {
            ctx.shadowBlur = size * 2
            ctx.shadowColor = this.color
            ctx.fill()
            ctx.shadowBlur = 0
          }
        }
      }
    }
    
    class GalaxyParticle {
      constructor() {
        this.angle = Math.random() * Math.PI * 2
        this.radius = Math.random() * Math.min(canvas.width, canvas.height) * 0.4
        this.x = canvas.width / 2 + Math.cos(this.angle) * this.radius
        this.y = canvas.height / 2 + Math.sin(this.angle) * this.radius
        this.size = Math.random() * 3 + 1
        this.speed = Math.random() * 0.02 + 0.005
        this.color = settings.darkMode ? 
          (Math.random() > 0.5 ? '#00e5ff' : '#4361ee') :
          (Math.random() > 0.5 ? '#818cf8' : '#6366f1')
        this.opacity = Math.random() * 0.6 + 0.2
        this.pulse = Math.random() * Math.PI * 2
      }
      
      update() {
        this.angle += this.speed
        this.pulse += 0.03
        this.x = canvas.width / 2 + Math.cos(this.angle) * this.radius
        this.y = canvas.height / 2 + Math.sin(this.angle) * this.radius
      }
      
      draw() {
        const pulseSize = this.size + Math.sin(this.pulse) * 0.5
        ctx.fillStyle = this.color
        ctx.globalAlpha = this.opacity + Math.sin(this.pulse) * 0.2
        ctx.beginPath()
        ctx.arc(this.x, this.y, pulseSize, 0, Math.PI * 2)
        ctx.fill()
        
        // Add glow effect
        ctx.shadowBlur = 8
        ctx.shadowColor = this.color
        ctx.fill()
        ctx.shadowBlur = 0
      }
    }
    
    const init = () => {
      // Initialize stars
      for (let i = 0; i < numberOfStars; i++) {
        starsArray.push(new Star())
      }
      
      // Initialize galaxy particles
      for (let i = 0; i < numberOfParticles; i++) {
        particlesArray.push(new GalaxyParticle())
      }
    }
    
    const drawGalaxySpiral = () => {
      const centerX = canvas.width / 2
      const centerY = canvas.height / 2
      const time = Date.now() * 0.0005
      
      ctx.globalAlpha = 0.3
      ctx.strokeStyle = settings.darkMode ? '#4361ee' : '#818cf8'
      ctx.lineWidth = 2
      
      // Draw multiple spiral arms
      for (let arm = 0; arm < 3; arm++) {
        ctx.beginPath()
        for (let i = 0; i < 100; i++) {
          const angle = (i * 0.1) + (arm * Math.PI * 2 / 3) + time
          const radius = i * 3
          const x = centerX + Math.cos(angle) * radius
          const y = centerY + Math.sin(angle) * radius
          
          if (i === 0) {
            ctx.moveTo(x, y)
          } else {
            ctx.lineTo(x, y)
          }
        }
        ctx.stroke()
      }
    }
    
    const drawNebula = () => {
      const centerX = canvas.width / 2
      const centerY = canvas.height / 2
      const time = Date.now() * 0.001
      
      // Create nebula effect with gradients
      const gradient = ctx.createRadialGradient(
        centerX, centerY, 0,
        centerX, centerY, Math.min(canvas.width, canvas.height) * 0.6
      )
      
      if (settings.darkMode) {
        gradient.addColorStop(0, 'rgba(0, 229, 255, 0.1)')
        gradient.addColorStop(0.3, 'rgba(67, 97, 238, 0.05)')
        gradient.addColorStop(0.6, 'rgba(157, 78, 221, 0.03)')
        gradient.addColorStop(1, 'rgba(0, 0, 0, 0)')
      } else {
        gradient.addColorStop(0, 'rgba(129, 140, 248, 0.1)')
        gradient.addColorStop(0.3, 'rgba(99, 102, 241, 0.05)')
        gradient.addColorStop(0.6, 'rgba(139, 92, 246, 0.03)')
        gradient.addColorStop(1, 'rgba(255, 255, 255, 0)')
      }
      
      ctx.fillStyle = gradient
      ctx.globalAlpha = 0.8 + Math.sin(time) * 0.2
      ctx.fillRect(0, 0, canvas.width, canvas.height)
    }
    
    const drawConnections = () => {
      for (let i = 0; i < particlesArray.length; i++) {
        for (let j = i + 1; j < particlesArray.length; j++) {
          const dx = particlesArray[i].x - particlesArray[j].x
          const dy = particlesArray[i].y - particlesArray[j].y
          const distance = Math.sqrt(dx * dx + dy * dy)
          
          if (distance < 120) {
            ctx.strokeStyle = settings.darkMode ? '#4361ee' : '#818cf8'
            ctx.globalAlpha = (120 - distance) / 120 * 0.15
            ctx.lineWidth = 1
            ctx.beginPath()
            ctx.moveTo(particlesArray[i].x, particlesArray[i].y)
            ctx.lineTo(particlesArray[j].x, particlesArray[j].y)
            ctx.stroke()
          }
        }
      }
    }
    
    const drawShootingStars = () => {
      const time = Date.now() * 0.001
      
      if (Math.random() < 0.005) { // Occasional shooting stars
        const startX = Math.random() * canvas.width
        const startY = Math.random() * canvas.height * 0.3
        const length = Math.random() * 100 + 50
        const angle = Math.random() * Math.PI / 4 + Math.PI / 4
        
        ctx.strokeStyle = '#ffffff'
        ctx.globalAlpha = 0.8
        ctx.lineWidth = 2
        ctx.beginPath()
        ctx.moveTo(startX, startY)
        ctx.lineTo(startX + Math.cos(angle) * length, startY + Math.sin(angle) * length)
        ctx.stroke()
        
        // Add glow trail
        ctx.shadowBlur = 10
        ctx.shadowColor = '#ffffff'
        ctx.stroke()
        ctx.shadowBlur = 0
      }
    }
    
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      
      // Draw nebula background
      drawNebula()
      
      // Draw galaxy spiral
      drawGalaxySpiral()
      
      // Update and draw stars (3D effect)
      for (let i = 0; i < starsArray.length; i++) {
        starsArray[i].update()
        starsArray[i].draw()
      }
      
      // Update and draw galaxy particles
      for (let i = 0; i < particlesArray.length; i++) {
        particlesArray[i].update()
        particlesArray[i].draw()
      }
      
      // Draw connections between particles
      drawConnections()
      
      // Occasional shooting stars
      drawShootingStars()
      
      // Draw subtle grid overlay
      ctx.globalAlpha = 0.02
      ctx.strokeStyle = settings.darkMode ? '#4361ee' : '#818cf8'
      ctx.lineWidth = 0.5
      
      for (let i = 0; i < canvas.height; i += 100) {
        ctx.beginPath()
        ctx.moveTo(0, i)
        ctx.lineTo(canvas.width, i)
        ctx.stroke()
      }
      
      for (let i = 0; i < canvas.width; i += 100) {
        ctx.beginPath()
        ctx.moveTo(i, 0)
        ctx.lineTo(i, canvas.height)
        ctx.stroke()
      }
      
      animationFrameId = requestAnimationFrame(animate)
    }
    
    init()
    animate()
    
    return () => {
      window.removeEventListener('resize', handleResize)
      cancelAnimationFrame(animationFrameId)
    }
  }, [settings.darkMode])

  return (
    <motion.div 
      className={`fixed inset-0 -z-10 ${
        settings.darkMode 
          ? 'bg-gradient-to-br from-space via-midnight to-space' 
          : 'bg-gradient-to-br from-indigo-50/50 via-white to-purple-50/30'
      } overflow-hidden`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 2 }}
    >
      <canvas 
        ref={canvasRef} 
        className="absolute inset-0 w-full h-full"
      />
      
      {/* Additional gradient overlays for depth */}
      <div className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-midnight/30" />
      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-neon-cyan/3 via-transparent to-neon-pink/3" />
      
      {/* Animated cosmic dust overlay */}
      <motion.div
        className="absolute inset-0 opacity-20"
        animate={{
          background: [
            'radial-gradient(circle at 20% 80%, rgba(0, 229, 255, 0.1) 0%, transparent 50%)',
            'radial-gradient(circle at 80% 20%, rgba(255, 56, 152, 0.1) 0%, transparent 50%)',
            'radial-gradient(circle at 40% 40%, rgba(67, 97, 238, 0.1) 0%, transparent 50%)',
          ]
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          repeatType: 'reverse'
        }}
      />
    </motion.div>
  )
}

export default Background